import React from "react";
import { useTranslation } from "react-i18next";
import styles from "./PageLoader.module.css";

/**
 * The IN-APP loading screen — the sibling of the branded boot splash.
 *
 * The splash (#splash, see public/index.html) owns the first load of any URL
 * and covers it end to end. This component owns everything after that: a
 * language change re-fetching the business, a booking step reloading, and so
 * on. It deliberately stays light and frosted rather than cloning the splash's
 * full-bleed brand gradient — a colour takeover mid-session reads as a page
 * change, not as loading — but it shares the splash's brand vocabulary: the
 * business's logo and the business's theme colour.
 *
 * Props:
 *   message  – primary text (already translated)
 *   subtitle – secondary text (already translated)
 *   inline   – if true, renders without fixed positioning (embeds inside a parent)
 *   logo     – optional business logo URL (e.g. from getSalonBoot()). No logo
 *              means no logo container at all, matching the splash — an empty
 *              placeholder square communicates nothing.
 */
const PageLoader = ({ message, subtitle, inline = false, logo }) => {
  const { i18n } = useTranslation();
  const isRtl = i18n.dir() === "rtl";

  return (
    <div
      className={`${styles.wrap} ${inline ? styles.inline : styles.fullPage}`}
      dir={isRtl ? "rtl" : "ltr"}
      aria-live="polite"
      aria-busy="true"
    >
      {logo && <img src={logo} alt="" className={styles.logo} />}
      <div className={styles.spinnerOuter}>
        <div className={styles.spinnerRing} />
        <div className={styles.spinnerCore} />
      </div>

      <div className={styles.dots} aria-hidden="true">
        <span className={styles.dot} />
        <span className={styles.dot} />
        <span className={styles.dot} />
      </div>

      {message && <p className={styles.message}>{message}</p>}
      {subtitle && <p className={styles.subtitle}>{subtitle}</p>}
    </div>
  );
};

export default PageLoader;
